import { getLucideSvgText, hasLucideIcon } from './lucideIconCatalog';
export const PLACEMARK_DEFAULT_ICON = 'MapPin';
// Composite billboard. Layout, top-down:
//   • soft drop shadow at the bottom (gives the badge "lift")
//   • tapered tail from the disc down to the anchor point
//   • colored disc with thin white outline
//   • white glyph centered on the disc
// verticalOrigin: BOTTOM in Cesium pins the canvas bottom to the terrain
// coordinate, and the tail tip is drawn at the canvas bottom — so the tip
// marks the exact world point (same convention as the comment avatar pin).
export const PLACEMARK_BILLBOARD_WIDTH = 48;
export const PLACEMARK_BILLBOARD_HEIGHT = 56;
const BADGE_CENTER_X = PLACEMARK_BILLBOARD_WIDTH / 2;
const BADGE_CENTER_Y = 24;
const BADGE_RADIUS = 20;
const BADGE_OUTLINE_WIDTH = 2;
const TAIL_HALF_WIDTH = 8;
const TAIL_BASE_Y = 38;
const TAIL_TIP_Y = PLACEMARK_BILLBOARD_HEIGHT - 2;
const GLYPH_SIZE = 24;
const GLYPH_OFFSET_X = BADGE_CENTER_X - GLYPH_SIZE / 2;
const GLYPH_OFFSET_Y = BADGE_CENTER_Y - GLYPH_SIZE / 2;
const GLYPH_STROKE_WIDTH = 2;
const SHADOW_CENTER_X = BADGE_CENTER_X;
const SHADOW_CENTER_Y = 50;
const SHADOW_RADIUS_X = 14;
const SHADOW_RADIUS_Y = 4;
// Far-zoom dot — swapped in via cesiumEntityFactory's CallbackProperty
// when the camera is high enough that the badge would be a useless smear.
export const PLACEMARK_FAR_DOT_SIZE = 16;
const FAR_DOT_RADIUS = 6;
// Label sits above the badge, not over it. The billboard grows with the
// user-set placemark scale, so the offset has to grow with it too.
export const placemarkLabelOffsetY = (scale = 1.0) => -(PLACEMARK_BILLBOARD_HEIGHT * scale) - 4;
const DEFAULT_VIEWBOX = { x: 0, y: 0, width: 24, height: 24 };
const parsedIconCache = new Map();
const canvasCache = new Map();
const glyphCanvasCache = new Map();
const farDotCanvasCache = new Map();
const CANVAS_CACHE_MAX = 200;
const unknownWarned = new Set();
// Backwards compatibility: existing placemarks may have a Maki name
// stored in their `icon` field. Map the names we exposed in the v1
// Maki picker to the closest Lucide equivalent so they keep rendering
// instead of falling back to MapPin for everyone.
const MAKI_TO_LUCIDE = {
    marker: 'MapPin',
    'marker-stroked': 'MapPin',
    circle: 'Circle',
    'circle-stroked': 'Circle',
    square: 'CircleParking',
    'square-stroked': 'CircleParking',
    triangle: 'Triangle',
    'triangle-stroked': 'Triangle',
    star: 'Star',
    'star-stroked': 'Star',
    cross: 'Plus',
    heart: 'Heart',
    'heart-stroked': 'Heart',
    embassy: 'Landmark',
    // Nature
    park: 'TreePine',
    'park-alt1': 'TreePine',
    garden: 'Flower',
    tree: 'TreeDeciduous',
    farm: 'Tractor',
    mountain: 'Mountain',
    volcano: 'MountainSnow',
    cave: 'Mountain',
    wetland: 'Waves',
    beach: 'Waves',
    natural: 'Leaf',
    forest: 'Trees',
    // Water
    harbor: 'Anchor',
    'drinking-water': 'Droplet',
    swimming: 'Waves',
    dam: 'Waves',
    waterfall: 'Waves',
    fishing: 'Fish',
    water: 'Waves',
    watermill: 'Waves',
    aquarium: 'Fish',
    marina: 'Sailboat',
    // Structure
    building: 'Building',
    'building-alt1': 'Building2',
    'residential-community': 'House',
    town: 'Building2',
    'town-hall': 'Landmark',
    'fire-station': 'Siren',
    hospital: 'Hospital',
    school: 'School',
    library: 'Library',
    'place-of-worship': 'Church',
    lighthouse: 'Landmark',
    monument: 'Landmark',
    college: 'School',
    bank: 'Building2',
    prison: 'Castle',
    cemetery: 'Church',
    'religious-buddhist': 'Church',
    'religious-christian': 'Church',
    'religious-jewish': 'Church',
    'religious-muslim': 'Church',
    'religious-shinto': 'Church',
    industry: 'Factory',
    warehouse: 'Warehouse',
    'observation-tower': 'Binoculars',
    castle: 'Castle',
    fence: 'House',
    // Recreation
    bicycle: 'Bike',
    hiking: 'Footprints',
    'horse-riding': 'Footprints',
    skiing: 'MountainSnow',
    'cross-country-skiing': 'MountainSnow',
    sledding: 'MountainSnow',
    snowmobile: 'MountainSnow',
    campsite: 'Tent',
    caravan: 'Caravan',
    viewpoint: 'Binoculars',
    'picnic-site': 'TentTree',
    attraction: 'Star',
    // Hazard
    danger: 'AlertTriangle',
    roadblock: 'Construction',
    barrier: 'Construction',
    // Transport
    car: 'Car',
    parking: 'CircleParking',
    'parking-garage': 'SquareParking',
    'parking-paid': 'ParkingMeter',
    fuel: 'Fuel',
    'charging-station': 'Fuel',
    airport: 'Plane',
    airfield: 'Plane',
    bus: 'Bus',
    rail: 'Train',
    ferry: 'Ship',
    bridge: 'Landmark',
    gate: 'DoorOpen',
    helicopter: 'Plane',
    // Service
    restaurant: 'Utensils',
    'fast-food': 'Utensils',
    cafe: 'Coffee',
    bar: 'Coffee',
    shop: 'Store',
    grocery: 'ShoppingCart',
    pharmacy: 'Stethoscope',
    hardware: 'Wrench',
    hospital_alt: 'Hospital',
    lodging: 'Hotel',
    doctor: 'Stethoscope',
    dentist: 'Stethoscope',
    veterinary: 'Dog',
    information: 'MapPin',
    toilet: 'DoorClosed',
    telephone: 'Phone',
    'mobile-phone': 'Phone',
    wifi: 'Wifi',
};
const parser = typeof DOMParser !== 'undefined' ? new DOMParser() : null;
const parseViewBox = (svgEl) => {
    const vb = svgEl.getAttribute('viewBox');
    if (!vb)
        return { ...DEFAULT_VIEWBOX };
    const parts = vb.split(/[\s,]+/).map(Number);
    if (parts.length !== 4 || parts.some(Number.isNaN))
        return { ...DEFAULT_VIEWBOX };
    return { x: parts[0], y: parts[1], width: parts[2], height: parts[3] };
};
// Resolve fill/stroke for a child shape with the parent <svg> as fallback.
// Lucide icons never set fill/stroke per shape — they inherit from the
// `fill="none" stroke="currentColor"` wrapper we emit in lucideIconCatalog.
const resolveShape = (el, svgFill, svgStroke) => {
    var _a, _b;
    const fillAttr = (_a = el.getAttribute('fill')) !== null && _a !== void 0 ? _a : svgFill;
    const strokeAttr = (_b = el.getAttribute('stroke')) !== null && _b !== void 0 ? _b : svgStroke;
    const fillSet = fillAttr !== null && fillAttr !== 'none';
    const strokeSet = strokeAttr !== null && strokeAttr !== 'none';
    return { shouldFill: fillSet, shouldStroke: strokeSet };
};
const elementToPath2D = (el, svgFill, svgStroke) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
    const tag = el.tagName.toLowerCase();
    const { shouldFill, shouldStroke } = resolveShape(el, svgFill, svgStroke);
    switch (tag) {
        case 'path': {
            const d = el.getAttribute('d');
            if (!d)
                return null;
            return { path: new Path2D(d), shouldFill, shouldStroke };
        }
        case 'circle': {
            const cx = Number((_a = el.getAttribute('cx')) !== null && _a !== void 0 ? _a : '0');
            const cy = Number((_b = el.getAttribute('cy')) !== null && _b !== void 0 ? _b : '0');
            const r = Number((_c = el.getAttribute('r')) !== null && _c !== void 0 ? _c : '0');
            if (!r)
                return null;
            const p = new Path2D();
            p.arc(cx, cy, r, 0, Math.PI * 2);
            return { path: p, shouldFill, shouldStroke };
        }
        case 'ellipse': {
            const cx = Number((_d = el.getAttribute('cx')) !== null && _d !== void 0 ? _d : '0');
            const cy = Number((_e = el.getAttribute('cy')) !== null && _e !== void 0 ? _e : '0');
            const rx = Number((_f = el.getAttribute('rx')) !== null && _f !== void 0 ? _f : '0');
            const ry = Number((_g = el.getAttribute('ry')) !== null && _g !== void 0 ? _g : '0');
            if (!rx || !ry)
                return null;
            const p = new Path2D();
            p.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
            return { path: p, shouldFill, shouldStroke };
        }
        case 'rect': {
            const x = Number((_h = el.getAttribute('x')) !== null && _h !== void 0 ? _h : '0');
            const y = Number((_j = el.getAttribute('y')) !== null && _j !== void 0 ? _j : '0');
            const w = Number((_k = el.getAttribute('width')) !== null && _k !== void 0 ? _k : '0');
            const h = Number((_l = el.getAttribute('height')) !== null && _l !== void 0 ? _l : '0');
            if (!w || !h)
                return null;
            const p = new Path2D();
            p.rect(x, y, w, h);
            return { path: p, shouldFill, shouldStroke };
        }
        case 'polygon':
        case 'polyline': {
            const points = ((_m = el.getAttribute('points')) !== null && _m !== void 0 ? _m : '').trim();
            if (!points)
                return null;
            const nums = points.split(/[\s,]+/).map(Number);
            if (nums.length < 4)
                return null;
            const p = new Path2D();
            p.moveTo(nums[0], nums[1]);
            for (let i = 2; i < nums.length - 1; i += 2)
                p.lineTo(nums[i], nums[i + 1]);
            if (tag === 'polygon')
                p.closePath();
            return {
                path: p,
                shouldFill: tag === 'polyline' ? false : shouldFill,
                shouldStroke,
            };
        }
        case 'line': {
            const x1 = Number((_o = el.getAttribute('x1')) !== null && _o !== void 0 ? _o : '0');
            const y1 = Number((_p = el.getAttribute('y1')) !== null && _p !== void 0 ? _p : '0');
            const x2 = Number((_q = el.getAttribute('x2')) !== null && _q !== void 0 ? _q : '0');
            const y2 = Number((_r = el.getAttribute('y2')) !== null && _r !== void 0 ? _r : '0');
            const p = new Path2D();
            p.moveTo(x1, y1);
            p.lineTo(x2, y2);
            return { path: p, shouldFill: false, shouldStroke: true };
        }
        default:
            return null;
    }
};
const parseIcon = (svgText) => {
    if (!parser)
        return null;
    const doc = parser.parseFromString(svgText, 'image/svg+xml');
    const svgEl = doc.documentElement;
    if (!svgEl || svgEl.tagName.toLowerCase() !== 'svg')
        return null;
    const viewBox = parseViewBox(svgEl);
    const svgFill = svgEl.getAttribute('fill');
    const svgStroke = svgEl.getAttribute('stroke');
    const shapes = [];
    const candidates = svgEl.querySelectorAll('path, circle, ellipse, rect, polygon, polyline, line');
    candidates.forEach((el) => {
        const result = elementToPath2D(el, svgFill, svgStroke);
        if (result)
            shapes.push(result);
    });
    return { viewBox, shapes };
};
const getParsedIcon = (iconName) => {
    if (parsedIconCache.has(iconName))
        return parsedIconCache.get(iconName);
    const text = getLucideSvgText(iconName);
    if (!text) {
        parsedIconCache.set(iconName, null);
        return null;
    }
    const parsed = parseIcon(text);
    parsedIconCache.set(iconName, parsed);
    return parsed;
};
const opacityBucket = (opacity) => Math.round(Math.max(0, Math.min(1, opacity)) * 20) / 20;
const cacheKey = (iconName, color, opacity) => `${iconName}|${color.toLowerCase()}|${opacityBucket(opacity)}`;
const evictOldest = (cache) => {
    if (cache.size <= CANVAS_CACHE_MAX)
        return;
    const firstKey = cache.keys().next().value;
    if (firstKey !== undefined)
        cache.delete(firstKey);
};
export const resolveIconName = (icon) => {
    if (!icon || !icon.trim())
        return PLACEMARK_DEFAULT_ICON;
    const trimmed = icon.trim();
    if (hasLucideIcon(trimmed))
        return trimmed;
    const migrated = MAKI_TO_LUCIDE[trimmed];
    if (migrated && hasLucideIcon(migrated))
        return migrated;
    if (!unknownWarned.has(trimmed)) {
        unknownWarned.add(trimmed);
        // eslint-disable-next-line no-console
        console.warn(`[placemarkIconRenderer] Unknown icon name "${trimmed}"; falling back to "${PLACEMARK_DEFAULT_ICON}".`);
    }
    return PLACEMARK_DEFAULT_ICON;
};
const createTransparentCanvas = (width, height) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
};
// Foreground color used on light badges (e.g. user picked white). Picked
// to read clearly without being pure-black harsh.
const DARK_FOREGROUND = '#1f1f1f';
const LIGHT_FOREGROUND = '#FFFFFF';
// sRGB relative luminance threshold above which we treat the badge as
// "light" and switch to a dark glyph + outline. 0.6 puts white, pale
// yellow, and pale-green clearly on the light side, dark red/forest/navy
// clearly on the dark side.
const LIGHT_BADGE_LUMINANCE_THRESHOLD = 0.6;
// Use the browser's color parser to normalize any CSS color string to
// "#rrggbb" or "rgba(...)". Reusing a single offscreen canvas avoids
// allocating one per call.
let colorParseCtx = null;
const parseRgb = (css) => {
    if (!colorParseCtx) {
        const c = document.createElement('canvas');
        colorParseCtx = c.getContext('2d');
    }
    if (!colorParseCtx)
        return { r: 0, g: 0, b: 0 };
    colorParseCtx.fillStyle = '#000000';
    colorParseCtx.fillStyle = css;
    const normalized = colorParseCtx.fillStyle;
    // Hex form: "#rrggbb"
    if (normalized.startsWith('#') && normalized.length === 7) {
        return {
            r: parseInt(normalized.slice(1, 3), 16),
            g: parseInt(normalized.slice(3, 5), 16),
            b: parseInt(normalized.slice(5, 7), 16),
        };
    }
    // rgba/rgb fallback
    const m = normalized.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (m)
        return { r: Number(m[1]), g: Number(m[2]), b: Number(m[3]) };
    return { r: 0, g: 0, b: 0 };
};
const srgbToLinear = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};
const relativeLuminance = (css) => {
    const { r, g, b } = parseRgb(css);
    return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
};
const isLightColor = (css) => relativeLuminance(css) > LIGHT_BADGE_LUMINANCE_THRESHOLD;
const drawShadow = (ctx, opacity) => {
    ctx.save();
    ctx.globalAlpha = 0.25 * opacity;
    ctx.beginPath();
    ctx.ellipse(SHADOW_CENTER_X, SHADOW_CENTER_Y, SHADOW_RADIUS_X, SHADOW_RADIUS_Y, 0, 0, Math.PI * 2);
    ctx.fillStyle = '#000000';
    ctx.fill();
    ctx.restore();
};
// Drawn before the disc so the disc + ring cover the tail base and the two
// read as one shape.
const drawTail = (ctx, color, opacity) => {
    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.beginPath();
    ctx.moveTo(BADGE_CENTER_X - TAIL_HALF_WIDTH, TAIL_BASE_Y);
    ctx.lineTo(BADGE_CENTER_X, TAIL_TIP_Y);
    ctx.lineTo(BADGE_CENTER_X + TAIL_HALF_WIDTH, TAIL_BASE_Y);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
    ctx.restore();
};
const drawBadge = (ctx, color, opacity, lightBadge) => {
    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.beginPath();
    ctx.arc(BADGE_CENTER_X, BADGE_CENTER_Y, BADGE_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = BADGE_OUTLINE_WIDTH;
    // Outline contrasts the badge: light badge → dark ring; dark badge → near-white ring.
    ctx.strokeStyle = lightBadge
        ? `rgba(31, 31, 31, ${0.65 * opacity})`
        : `rgba(255, 255, 255, ${0.95 * opacity})`;
    ctx.stroke();
    ctx.restore();
};
const drawGlyph = (ctx, parsed, opacity, foreground) => {
    ctx.save();
    ctx.globalAlpha = opacity;
    const scale = GLYPH_SIZE / Math.max(parsed.viewBox.width, parsed.viewBox.height);
    ctx.translate(GLYPH_OFFSET_X, GLYPH_OFFSET_Y);
    ctx.scale(scale, scale);
    ctx.translate(-parsed.viewBox.x, -parsed.viewBox.y);
    ctx.fillStyle = foreground;
    ctx.strokeStyle = foreground;
    ctx.lineWidth = GLYPH_STROKE_WIDTH;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const shape of parsed.shapes) {
        if (shape.shouldFill)
            ctx.fill(shape.path);
        if (shape.shouldStroke)
            ctx.stroke(shape.path);
    }
    ctx.restore();
};
const buildBadgeCanvas = (iconName, color, opacity, withShadow = true) => {
    const canvas = createTransparentCanvas(PLACEMARK_BILLBOARD_WIDTH, PLACEMARK_BILLBOARD_HEIGHT);
    const ctx = canvas.getContext('2d');
    if (!ctx)
        return canvas;
    const lightBadge = isLightColor(color);
    const glyphColor = lightBadge ? DARK_FOREGROUND : LIGHT_FOREGROUND;
    // Print-legend tiles (withShadow=false) stay a plain disc — the tail only
    // makes sense on the map where it points at the anchor.
    if (withShadow) {
        drawShadow(ctx, opacity);
        drawTail(ctx, color, opacity);
    }
    drawBadge(ctx, color, opacity, lightBadge);
    const parsed = getParsedIcon(iconName);
    if (parsed)
        drawGlyph(ctx, parsed, opacity, glyphColor);
    return canvas;
};
// Cached read-only badge canvas. Caller MUST NOT mutate — Cesium shares
// GPU textures by image reference, so in-place mutation would corrupt
// every billboard using the same (icon, color, opacity) combo.
// Comment pin badge: same shape as a placemark badge, but with an explicit
// stroke color (typically derived from the comment's first tag). When
// strokeColor is null, fall back to the placemark's default contrast ring.
// No glyph is drawn — the comment icon is signalled at the pin level by
// fill+stroke alone (a future pin-shape redesign is tracked separately).
const COMMENT_OUTLINE_WIDTH = 4;
const commentCanvasCache = new Map();
const drawCommentBadge = (ctx, fillColor, strokeColor, opacity, lightBadge) => {
    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.beginPath();
    ctx.arc(BADGE_CENTER_X, BADGE_CENTER_Y, BADGE_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = fillColor;
    ctx.fill();
    ctx.lineWidth = strokeColor ? COMMENT_OUTLINE_WIDTH : BADGE_OUTLINE_WIDTH;
    ctx.strokeStyle = strokeColor
        ? strokeColor
        : lightBadge
            ? `rgba(31, 31, 31, ${0.65 * opacity})`
            : `rgba(255, 255, 255, ${0.95 * opacity})`;
    ctx.stroke();
    ctx.restore();
};
const buildCommentBadgeCanvas = (fillColor, strokeColor, opacity) => {
    const canvas = createTransparentCanvas(PLACEMARK_BILLBOARD_WIDTH, PLACEMARK_BILLBOARD_HEIGHT);
    const ctx = canvas.getContext('2d');
    if (!ctx)
        return canvas;
    drawShadow(ctx, opacity);
    drawCommentBadge(ctx, fillColor, strokeColor, opacity, isLightColor(fillColor));
    return canvas;
};
// Comment avatar canvas: a speech-bubble shape (round body + downward tail)
// with the author's initial drawn in the bubble. Tail tip sits near the
// bottom-center of the canvas so that, with verticalOrigin: BOTTOM, the
// tip lands at the world anchor (fixes the off-center-icon complaint).
// Figma-style pin: a dark "shell" (bubble + tail) frames a smaller
// user-color avatar nested inside. The optional tag ring is drawn as a
// thin stroke on the inner avatar — preserving the tag signal without
// dominating the pin's silhouette.
const COMMENT_AVATAR_BUBBLE_CENTER_X = PLACEMARK_BILLBOARD_WIDTH / 2;
export const COMMENT_AVATAR_BUBBLE_CENTER_Y = 20;
export const COMMENT_AVATAR_BUBBLE_RADIUS = 18;
export const COMMENT_AVATAR_INNER_RADIUS = 12;
const COMMENT_AVATAR_TAIL_LEFT_X = COMMENT_AVATAR_BUBBLE_CENTER_X - 9;
const COMMENT_AVATAR_TAIL_RIGHT_X = COMMENT_AVATAR_BUBBLE_CENTER_X + 9;
const COMMENT_AVATAR_TAIL_BASE_Y = 33;
const COMMENT_AVATAR_TAIL_TIP_X = COMMENT_AVATAR_BUBBLE_CENTER_X;
const COMMENT_AVATAR_TAIL_TIP_Y = PLACEMARK_BILLBOARD_HEIGHT - 2;
const COMMENT_AVATAR_RING_WIDTH = 2;
// Charcoal shell, mirrors var(--charcoal) from styles.css.
const COMMENT_AVATAR_SHELL_COLOR = '#181818';
const commentAvatarCanvasCache = new Map();
const drawCommentAvatarTail = (ctx, fillColor) => {
    ctx.beginPath();
    ctx.moveTo(COMMENT_AVATAR_TAIL_LEFT_X, COMMENT_AVATAR_TAIL_BASE_Y);
    ctx.lineTo(COMMENT_AVATAR_TAIL_TIP_X, COMMENT_AVATAR_TAIL_TIP_Y);
    ctx.lineTo(COMMENT_AVATAR_TAIL_RIGHT_X, COMMENT_AVATAR_TAIL_BASE_Y);
    ctx.closePath();
    ctx.fillStyle = fillColor;
    ctx.fill();
};
const drawCommentAvatarShell = (ctx, opacity) => {
    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.beginPath();
    ctx.arc(COMMENT_AVATAR_BUBBLE_CENTER_X, COMMENT_AVATAR_BUBBLE_CENTER_Y, COMMENT_AVATAR_BUBBLE_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = COMMENT_AVATAR_SHELL_COLOR;
    ctx.fill();
    ctx.restore();
};
const drawCommentAvatarInner = (ctx, fillColor, ringColor, opacity) => {
    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.beginPath();
    ctx.arc(COMMENT_AVATAR_BUBBLE_CENTER_X, COMMENT_AVATAR_BUBBLE_CENTER_Y, COMMENT_AVATAR_INNER_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = fillColor;
    ctx.fill();
    if (ringColor) {
        ctx.lineWidth = COMMENT_AVATAR_RING_WIDTH;
        ctx.strokeStyle = ringColor;
        ctx.stroke();
    }
    ctx.restore();
};
const drawCommentAvatarInitial = (ctx, initial, fgColor, opacity) => {
    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.fillStyle = fgColor;
    ctx.font = 'bold 13px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initial.slice(0, 2), COMMENT_AVATAR_BUBBLE_CENTER_X, COMMENT_AVATAR_BUBBLE_CENTER_Y + 1);
    ctx.restore();
};
const buildCommentAvatarCanvas = (initial, fillColor, fgColor, ringColor, opacity) => {
    const canvas = createTransparentCanvas(PLACEMARK_BILLBOARD_WIDTH, PLACEMARK_BILLBOARD_HEIGHT);
    const ctx = canvas.getContext('2d');
    if (!ctx)
        return canvas;
    drawShadow(ctx, opacity);
    // Tail in the same charcoal as the shell so they read as one shape.
    drawCommentAvatarTail(ctx, COMMENT_AVATAR_SHELL_COLOR);
    drawCommentAvatarShell(ctx, opacity);
    drawCommentAvatarInner(ctx, fillColor, ringColor, opacity);
    drawCommentAvatarInitial(ctx, initial, fgColor, opacity);
    return canvas;
};
export const getCommentAvatarCanvas = (initial, fillColor, fgColor, ringColor, opacity = 1.0) => {
    const key = `${initial}|${fillColor.toLowerCase()}|${fgColor.toLowerCase()}|${ringColor ? ringColor.toLowerCase() : 'no-ring'}|${opacityBucket(opacity)}`;
    const cached = commentAvatarCanvasCache.get(key);
    if (cached) {
        commentAvatarCanvasCache.delete(key);
        commentAvatarCanvasCache.set(key, cached);
        return cached;
    }
    const canvas = buildCommentAvatarCanvas(initial, fillColor, fgColor, ringColor, opacity);
    commentAvatarCanvasCache.set(key, canvas);
    evictOldest(commentAvatarCanvasCache);
    return canvas;
};
export const getCommentBadgeCanvas = (fillColor, strokeColor, opacity = 1.0) => {
    const key = `${fillColor.toLowerCase()}|${strokeColor ? strokeColor.toLowerCase() : 'default'}|${opacityBucket(opacity)}`;
    const cached = commentCanvasCache.get(key);
    if (cached) {
        commentCanvasCache.delete(key);
        commentCanvasCache.set(key, cached);
        return cached;
    }
    const canvas = buildCommentBadgeCanvas(fillColor, strokeColor, opacity);
    commentCanvasCache.set(key, canvas);
    evictOldest(commentCanvasCache);
    return canvas;
};
export const getPlacemarkIconCanvas = (iconName, color, opacity = 1.0, withShadow = true) => {
    const resolved = resolveIconName(iconName);
    const key = cacheKey(resolved, color, opacity) + (withShadow ? '' : '|ns');
    const cached = canvasCache.get(key);
    if (cached) {
        canvasCache.delete(key);
        canvasCache.set(key, cached);
        return cached;
    }
    const canvas = buildBadgeCanvas(resolved, color, opacity, withShadow);
    canvasCache.set(key, canvas);
    evictOldest(canvasCache);
    return canvas;
};
// Glyph-only canvas for picker tiles. No badge, no shadow — just the
// stroked Lucide glyph tinted in the requested color so picker tiles
// match the dialog's selected colour without the colored disc dominating.
const GLYPH_PREVIEW_SIZE = 32;
const buildGlyphPreviewCanvas = (iconName, color) => {
    const canvas = createTransparentCanvas(GLYPH_PREVIEW_SIZE, GLYPH_PREVIEW_SIZE);
    const ctx = canvas.getContext('2d');
    if (!ctx)
        return canvas;
    const parsed = getParsedIcon(iconName);
    if (!parsed)
        return canvas;
    ctx.save();
    const scale = GLYPH_PREVIEW_SIZE / Math.max(parsed.viewBox.width, parsed.viewBox.height);
    ctx.scale(scale, scale);
    ctx.translate(-parsed.viewBox.x, -parsed.viewBox.y);
    ctx.fillStyle = color;
    ctx.strokeStyle = color;
    ctx.lineWidth = GLYPH_STROKE_WIDTH;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const shape of parsed.shapes) {
        if (shape.shouldFill)
            ctx.fill(shape.path);
        if (shape.shouldStroke)
            ctx.stroke(shape.path);
    }
    ctx.restore();
    return canvas;
};
export const getPlacemarkGlyphCanvas = (iconName, color) => {
    const resolved = resolveIconName(iconName);
    const key = `${resolved}|${color.toLowerCase()}`;
    const cached = glyphCanvasCache.get(key);
    if (cached) {
        glyphCanvasCache.delete(key);
        glyphCanvasCache.set(key, cached);
        return cached;
    }
    const canvas = buildGlyphPreviewCanvas(resolved, color);
    glyphCanvasCache.set(key, canvas);
    evictOldest(glyphCanvasCache);
    return canvas;
};
// Far-zoom dot canvas. The Cesium billboard.image callback swaps to this
// when the camera is above the dot-threshold altitude.
const buildFarDotCanvas = (color, opacity) => {
    const canvas = createTransparentCanvas(PLACEMARK_FAR_DOT_SIZE, PLACEMARK_FAR_DOT_SIZE);
    const ctx = canvas.getContext('2d');
    if (!ctx)
        return canvas;
    const lightDot = isLightColor(color);
    ctx.save();
    ctx.globalAlpha = opacity;
    ctx.beginPath();
    ctx.arc(PLACEMARK_FAR_DOT_SIZE / 2, PLACEMARK_FAR_DOT_SIZE / 2, FAR_DOT_RADIUS, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = lightDot
        ? `rgba(31, 31, 31, ${0.7 * opacity})`
        : `rgba(255, 255, 255, ${0.95 * opacity})`;
    ctx.stroke();
    ctx.restore();
    return canvas;
};
export const getPlacemarkFarDotCanvas = (color, opacity = 1.0) => {
    const key = `${color.toLowerCase()}|${opacityBucket(opacity)}`;
    const cached = farDotCanvasCache.get(key);
    if (cached) {
        farDotCanvasCache.delete(key);
        farDotCanvasCache.set(key, cached);
        return cached;
    }
    const canvas = buildFarDotCanvas(color, opacity);
    farDotCanvasCache.set(key, canvas);
    evictOldest(farDotCanvasCache);
    return canvas;
};
